<template>
  <message-dialog
    v-model="dialogOpen"
    :persistent="loading"
    :title="title"
    with-close
    :maximized="windowWidth < 768 && sendEmail"
    :column="!onlySend"
  >
    <template #default>
      <slot>
        <div class="text-h6 q-mb-sm">
          Send Email
        </div>
        <div class="text-body2 text-grey-7 q-mb-md">
          Choose who should receive this document.
        </div>
      </slot>

      <q-list v-if="sendEmail" class="q-gutter-sm">
        <slot name="send" />

        <!-- Channel selector (opt-in via allow-sms) -->
        <q-item v-if="allowSms">
          <q-item-section>
            <q-btn-toggle
              v-model="channel"
              :options="channelOptions"
              spread
              no-caps
              unelevated
              dense
              toggle-color="primary"
              color="grey-3"
              text-color="grey-8"
            />
          </q-item-section>
        </q-item>

        <q-item v-show="channelIncludesEmail">
          <q-item-section>
            <q-select
              :model-value="selectedEmails"
              :error="!loadingSuggestions && (error || !emailInput)"
              :autofocus="$q.platform.is.desktop"
              :loading="loadingSuggestions"
              :max-values="10"
              :use-input="selectedEmails.length < 10"
              :input-debounce="0"
              type="email"
              outlined
              stack-label
              hide-bottom-space
              clearable
              label="Send to Email Addresses"
              :error-message="!emailInput ? 'Please enter a valid email' : 'Please check that all entered emails are valid'"
              hint="Press enter to add an email"
              counter
              use-chips
              multiple
              hide-dropdown-icon
              new-value-mode="add-unique"
              @new-value="createNewValue"
              @remove="onClickSuggestion($event.value)"
              @clear="emailInput = null"
              @input-value="onInputNewEmail"
              @blur="onBlurEmailSelection"
            >
              <template #counter>
                <span :class="{'text-blue-5': !error && !!emailInput}">
                  {{ selectedEmails.length }} {{ pluraliseString('email', selectedEmails.length) }}
                </span>
              </template>
            </q-select>
          </q-item-section>
        </q-item>

        <template v-if="channelIncludesEmail && showSuggestionList">
          <q-separator inset />
          <EmailSuggestionList
            :model-value="selectedEmails"
            :list="suggestionList"
            multiple
            @update:model-value="onClickSuggestion"
          />
        </template>

        <!-- Mobile number (SMS) -->
        <template v-if="channelIncludesSms">
          <q-item>
            <q-item-section>
              <q-input
                v-model="phoneInput"
                outlined
                stack-label
                hide-bottom-space
                clearable
                type="tel"
                inputmode="tel"
                label="Send to Mobile Number"
                placeholder="e.g. +61 400 000 000"
                :loading="loadingPhones"
                :error="!phoneInput"
                error-message="Please enter a mobile number"
                hint="A link to the document is sent to this number"
              />
            </q-item-section>
          </q-item>
          <q-item>
            <q-item-section>
              <SmsChargeWarning />
              <SandboxSmsNotice v-if="isSandbox" />
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </template>

    <template #actions>
      <template v-if="!onlySend">
        <!-- Cancel -->
        <q-btn
          v-close-popup
          flat
          color="black"
          align="left"
          padding="md md"
          icon="mdi-arrow-left"
          :label="cancelText"
        />

        <q-separator inset />

        <q-btn
          align="left"
          padding="md md"
          :flat="!(loading && !sendEmail)"
          :icon="actionIcon"
          :label="'Only ' + actionName"
          :color="color + '-7'"
          @click="onClickOption(false)"
        />

        <q-btn
          padding="md md"
          align="left"
          autofocus
          icon="mdi-email-arrow-right"
          :flat="!(loading && sendEmail)"
          :label="sendActionLabel"
          :color="color + '-9'"
          @click="onClickSend"
        />
      </template>
      <template v-else>
        <q-btn
          padding="sm md"
          autofocus
          icon="mdi-email-arrow-right"
          :label="allowSms ? sendActionLabel : sendButtonLabel"
          unelevated
          color="primary"
          :disable="!canSubmitSend"
          @click="onClickOption(true)"
        />
      </template>
    </template>

    <template #after>
      <slot name="after" />
      <q-inner-loading
        :showing="loading"
        style="z-index: 1;"
      >
        <loading-spinner />
      </q-inner-loading>
    </template>
  </message-dialog>
</template>

<script>
import EmailSuggestionList from 'components/partials/communications/email/EmailSuggestionList'
import SmsChargeWarning from 'components/partials/communications/SmsChargeWarning.vue'
import SandboxSmsNotice from 'components/partials/sandbox/SandboxSmsNotice.vue'
import { findEmail, findPhone } from 'components/partials/PhoneAddressEmail'

/**
 * This partial is used for sending an email (with the option of:
 *    back, onlY perform action, perform action and send email)
 *
 * SLOTS:
 * - default: message to display
 * - after: (inherited from MessageDialog) - alternative content to display
 * - send: content to display inside of the Save & Send slot
 *
 * EMITS:
 * - complete: when the dialog is submit
 */
export default {
  components: {
    EmailSuggestionList,
    SmsChargeWarning,
    SandboxSmsNotice
  },
  props: {
    title: {
      type: String,
      default: 'Confirm'
    },
    cancelText: {
      type: String,
      default: 'Back'
    },
    color: {
      type: String,
      default: 'blue'
    },
    // name of the main action being performed (such as save, regenerate)
    actionName: {
      type: String,
      default: 'Save'
    },
    // Main action icon
    actionIcon: {
      type: String,
      default: 'mdi-check'
    },
    // Optionally specify a client who the email is being sent to
    client: {
      type: Object
    },
    // Force dialog into send-only mode
    onlySend: {
      type: Boolean,
      default: false
    },
    // Optionally use the dialog without a bound client
    requiresClient: {
      type: Boolean,
      default: false
    },
    // An optional saved email separate from the client itself
    savedEmail: {
      type: String
    },
    // Opt-in: allow sending the document by SMS (channel selector + mobile field)
    allowSms: {
      type: Boolean,
      default: false
    },
    // An optional saved mobile number separate from the client itself
    savedPhone: {
      type: String
    },
    // Order to use for selecting default email
    emailOrder: {
      type: Array,
      default: () => {
        return ['PRIMARY', 'BILLING', 'EXTRA']
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  emits: ['complete'],
  data () {
    return {
      dialogOpen: false,
      sendEmail: false,
      emailInput: null,
      addEmailToListInput: null,
      channel: 'email',
      phoneInput: null
    }
  },
  computed: {
    channelOptions () {
      return [
        { label: 'Email', value: 'email' },
        { label: 'SMS', value: 'sms' },
        { label: 'Both', value: 'both' }
      ]
    },
    channelIncludesEmail () {
      // Without the SMS opt-in the dialog is always email
      return !this.allowSms || this.channel === 'email' || this.channel === 'both'
    },
    channelIncludesSms () {
      return this.allowSms && (this.channel === 'sms' || this.channel === 'both')
    },
    sendActionLabel () {
      if (!this.allowSms) {
        return 'Send Email'
      }
      if (this.channel === 'sms') {
        return 'Send SMS'
      }
      if (this.channel === 'both') {
        return 'Send Email & SMS'
      }
      return 'Send Email'
    },
    isSandbox () {
      return !!process.env.SANDBOX
    },
    loadingPhones () {
      if (!this.client) {
        return false
      }
      return this.$store.getters['clientSuggestionLoader/isLoadingPhones'](this.client.id)
    },
    phoneSuggestionList () {
      if (!this.client) {
        return []
      }
      return this.$store.getters['clientSuggestionLoader/getPhones'](this.client.id) || []
    },
    // Whether the chosen channel(s) have the recipient(s) they need
    canSubmitSend () {
      if (this.channelIncludesEmail && !this.selectedEmails.length) {
        return false
      }
      if (this.channelIncludesSms && !this.phoneInput) {
        return false
      }
      return true
    },
    selectedEmails () {
      const onlyUnique = (value, index, array) => {
        return array.indexOf(value) === index
      }
      const input = this.emailInput || ''
      if (!input) {
        return []
      }
      return input.split(';').map(item => item.trim().toLowerCase()).filter(onlyUnique)
    },
    cleanedSelectedEmails () {
      return this.selectedEmails.join(';')
    },
    // Don't show the suggestion list if the only option is the value they have entered
    showSuggestionList () {
      return this.suggestionList.length > 0
    },
    loadingSuggestions () {
      if (!this.client) {
        return false
      }
      return this.$store.getters['clientSuggestionLoader/isLoadingEmails'](this.client.id)
    },
    // Generate list of suggested emails to show
    suggestionList () {
      const list = []

      if (this.client) {
        const emailResults = this.$store.getters['clientSuggestionLoader/getEmails'](this.client.id)
        for (const ele of emailResults) {
          list.push(ele)
        }
      }
      return list
    },
    // Only true if no emails have been selected and they have entered a valid email in the input box
    isNewEmailInputValid () {
      if (!this.addEmailToListInput || this.selectedEmails.length > 0) {
        return false
      }
      return this.checkEmailValid(this.addEmailToListInput)
    },
    sendButtonLabel () {
      const count = this.selectedEmails.length
      return count > 1 ? 'Send Emails' : 'Send Email'
    }
  },
  methods: {
    open (preventAction = false, defaultSend = false) {
      // Option to prevent showing the dialog and auto-save with email = false
      if (preventAction || (this.requiresClient && !this.client)) {
        this.onClickOption(false)
        return
      }
      this.dialogOpen = true
      this.sendEmail = this.onlySend || defaultSend
      this.autoSendTriggered = false

      // Prefill email when sending is enabled
      if (this.sendEmail || this.onlySend) {
        const populateEmail = () => {
          this.setDefaultEmailAddress()
        }

        if (this.client) {
          this.$store.dispatch('clientSuggestionLoader/loadEmails', this.client.id)
            .then(populateEmail)
            .catch(populateEmail)
        }
        else {
          populateEmail()
        }
      }

      // Prefill the mobile + default channel when SMS is enabled
      if (this.allowSms) {
        this.setDefaultChannel()
        if (this.client) {
          this.$store.dispatch('clientSuggestionLoader/loadPhones', this.client.id)
            .then(() => this.setDefaultPhone())
            .catch(() => this.setDefaultPhone())
        }
        else {
          this.setDefaultPhone()
        }
      }
    },
    setDefaultChannel () {
      const map = { EMAIL: 'email', SMS: 'sms', BOTH: 'both' }
      this.channel = map[this.client?.default_communication_method] || 'email'
    },
    setDefaultPhone () {
      if (this.savedPhone) {
        this.phoneInput = this.savedPhone
        return
      }
      const mobile = findPhone(
        this.phoneSuggestionList.filter(item => typeof item === 'object' && item.client_id === this.client?.id),
        'MOBILE', 'BUSINESS', 'DEFAULT'
      )
      if (mobile) {
        this.phoneInput = mobile.full_phone
      }
      else if (this.client?.primary_phone) {
        this.phoneInput = this.client.primary_phone.full_phone
      }
    },
    // Fetch default email to show in the input box
    setDefaultEmailAddress () {
      if (this.savedEmail) {
        // Attempt to prettify the saved emails
        this.emailInput = this.savedEmail.split(';').join('; ')
      }
      else if (this.client) {
        // filter suggestion list and attempt to find emails in priority order
        const tempEmail = findEmail(
          this.suggestionList.filter(item => typeof item === 'object' && !!item.type && item.client_id === this.client.id),
          ...this.emailOrder
        )
        if (tempEmail) {
          this.emailInput = tempEmail.email_address
        }
        else if (this.client.email_address) {
          this.emailInput = this.client.email_address
        }
      }
    },
    // Main save handler for submitting data from this dialog
    onClickOption (send = false) {
      // When SMS is enabled, "send" maps to the chosen channel(s)
      const wantEmail = send && this.channelIncludesEmail
      const wantSms = send && this.channelIncludesSms

      if (send && !this.canSubmitSend) {
        return
      }
      this.sendEmail = send

      const payload = {
        sendEmail: wantEmail,
        recipient: wantEmail ? this.cleanedSelectedEmails : null,
        sendSms: wantSms,
        phone: wantSms ? this.phoneInput : null
      }

      const doneFunc = () => {
        this.dialogOpen = false
      }

      this.$emit('complete', payload, doneFunc)
    },
    onClickSend () {
      // If send mode not enabled yet, enable and prefill before submitting
      if (!this.sendEmail) {
        this.sendEmail = true
        const populateEmail = () => {
          this.setDefaultEmailAddress()
        }
        if (this.client) {
          this.$store.dispatch('clientSuggestionLoader/loadEmails', this.client.id)
            .then(populateEmail)
            .catch(populateEmail)
        }
        else {
          populateEmail()
        }
        return
      }

      this.onClickOption(true)
    },
    onClickSuggestion (email) {
      const arr = this.selectedEmails

      const index = arr.indexOf(email)
      if (index > -1) {
        arr.splice(index, 1)
        this.emailInput = arr.join('; ')
      }
      else {
        arr.push(email)
        this.emailInput = arr.join('; ')
      }
    },
    // Save the unfinished new email to the state for auto-adding when QSelect is blurred
    onInputNewEmail (val) {
      this.addEmailToListInput = val
    },
    // When creating a new email to use, ensure it is valid (regex check)
    createNewValue (val, done) {
      if (!this.checkEmailValid(val)) {
        return
      }

      this.onClickSuggestion(val)
      done()
    },
    // If the user has not entered any emails and the user has typed in the new email box,
    //  attempt to auto-add it to the email selection list when input field is blurred.
    // This is to simulate the same behaviour prior to using QSelect here where they simply
    //  entered a single email into a QInput with the need to press enter
    onBlurEmailSelection () {
      const val = this.addEmailToListInput
      if (!this.isNewEmailInputValid) {
        return
      }

      this.onClickSuggestion(val)
    }
  }
}
</script>
